/**
 * Visitor Events Hook
 *
 * Processes events data for visitor display with 60-second refresh.
 * All times are venue-local - no timezone conversion needed.
 */

import { useMemo, useState, useEffect, useCallback } from "react";
import {
  getEventStatus,
  getNextOccurrence,
  type EventForSchedule,
  type EventStatus,
} from "../../lib/event-schedule";
import type { SponsorRef } from "@wondermaps/db";

export interface VisitorEvent extends EventForSchedule {
  name: string;
  description: string | null;
  poiId: string | null;
  headlinerName: string | null;
  headlinerLogoUrl: string | null;
  eventUrl: string | null;
  eventUrlLabel: string | null;
  isActive: boolean;
  // Sponsor linked via map_sponsor_events
  sponsorRef: SponsorRef | null;
  // Computed status
  status: EventStatus;
}

export interface UseVisitorEventsOptions {
  /** Refresh interval in ms (default: 60000 = 60 seconds) */
  refreshIntervalMs?: number;
}

export interface UseVisitorEventsReturn {
  /** Map of POI ID -> events at that POI */
  poiEventsMap: Map<string, VisitorEvent[]>;
  /** Get events for a specific POI */
  getPoiEvents: (poiId: string) => VisitorEvent[];

  /** Events happening right now */
  happeningNowEvents: VisitorEvent[];
  /** Set of POI IDs with events happening now */
  happeningNowPoiIds: Set<string>;
  /** Count of events happening now */
  happeningNowCount: number;

  /** All events occurring today */
  todaysEvents: VisitorEvent[];
  /** Count of today's events */
  todaysEventsCount: number;

  /** Events in the future (not today) */
  upcomingEvents: VisitorEvent[];

  /** Last time statuses were recalculated */
  lastRefreshedAt: Date;
}

/**
 * Process raw event data for visitor display
 *
 * @param events Raw event data from API (active events only)
 * @param options Configuration options
 */
export function useVisitorEvents(
  events: EventForSchedule[] | undefined,
  options: UseVisitorEventsOptions = {}
): UseVisitorEventsReturn {
  const { refreshIntervalMs = 60000 } = options;

  // Track refresh time to trigger re-computation
  const [refreshTick, setRefreshTick] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(new Date());

  // Set up refresh interval
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTick((t) => t + 1);
      setLastRefreshedAt(new Date());
    }, refreshIntervalMs);

    return () => clearInterval(interval);
  }, [refreshIntervalMs]);

  // Process events with status
  const processedEvents = useMemo(() => {
    if (!events?.length) return [];

    const now = new Date();
    return events.map((event) => ({
      ...event,
      status: getEventStatus(event, now),
    })) as VisitorEvent[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, refreshTick]);

  // Build POI -> events map
  const poiEventsMap = useMemo(() => {
    const map = new Map<string, VisitorEvent[]>();

    for (const event of processedEvents) {
      if (!event.poiId) continue;

      const existing = map.get(event.poiId) || [];
      existing.push(event);
      map.set(event.poiId, existing);
    }

    // Sort events within each POI by status priority then time
    for (const [poiId, poiEvents] of map) {
      map.set(poiId, sortEventsByPriority(poiEvents));
    }

    return map;
  }, [processedEvents]);

  // Get events for a specific POI
  const getPoiEvents = useCallback(
    (poiId: string): VisitorEvent[] => {
      return poiEventsMap.get(poiId) || [];
    },
    [poiEventsMap]
  );

  // Filter events by status
  const { happeningNowEvents, todaysEvents, upcomingEvents } = useMemo(() => {
    const happeningNow: VisitorEvent[] = [];
    const todays: VisitorEvent[] = [];
    const upcoming: VisitorEvent[] = [];

    for (const event of processedEvents) {
      switch (event.status) {
        case "happening-now":
          happeningNow.push(event);
          todays.push(event);
          break;
        case "today-upcoming":
          todays.push(event);
          break;
        case "today-past":
          // Don't include past events in any list
          break;
        case "upcoming":
          upcoming.push(event);
          break;
        case "past":
          // Skip
          break;
      }
    }

    return {
      happeningNowEvents: sortEventsByPriority(happeningNow),
      todaysEvents: sortEventsByPriority(todays),
      upcomingEvents: sortEventsByPriority(upcoming),
    };
  }, [processedEvents]);

  // Build set of POI IDs with happening now events
  const happeningNowPoiIds = useMemo(() => {
    const ids = new Set<string>();
    for (const event of happeningNowEvents) {
      if (event.poiId) {
        ids.add(event.poiId);
      }
    }
    return ids;
  }, [happeningNowEvents]);

  return {
    poiEventsMap,
    getPoiEvents,
    happeningNowEvents,
    happeningNowPoiIds,
    happeningNowCount: happeningNowEvents.length,
    todaysEvents,
    todaysEventsCount: todaysEvents.length,
    upcomingEvents,
    lastRefreshedAt,
  };
}

/**
 * Sort events: happening now first, then by next-occurrence date, then by time-of-day.
 * `startTime` alone is just time-of-day (e.g. "11:00:00") — comparing that ignores
 * actual date, which produces apparently-random ordering for recurring events
 * spanning multiple weeks. Use getNextOccurrence().date as the primary key.
 */
function sortEventsByPriority(events: VisitorEvent[]): VisitorEvent[] {
  const now = new Date();
  return [...events].sort((a, b) => {
    if (a.status === "happening-now" && b.status !== "happening-now") return -1;
    if (b.status === "happening-now" && a.status !== "happening-now") return 1;

    const aDate = getNextOccurrence(a, now)?.date ?? "";
    const bDate = getNextOccurrence(b, now)?.date ?? "";
    if (aDate !== bDate) return aDate.localeCompare(bDate);

    return a.startTime.localeCompare(b.startTime);
  });
}
